import PropTypes from 'prop-types';
import { format, parseISO } from 'date-fns';
import _ from 'lodash';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  Stack,
  IconButton
} from '@mui/material';
import logo from 'assets/images/vivekanand.png';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { useHospitalDetails } from 'api/cutsom-hooks';
import { normalizeHospitalDetails, pickHospitalLogo, pickHospitalPhone } from 'utils/gst';
import { getInvoiceInsurerName, getInvoiceVisitDate } from 'utils/invoiceBillingProvider';
import { numberToWords } from './utils';

export default function LabInvoiceA4Content({ customerInfo, invoiceData, handlePrint, onClose }) {
  const {
    invoice_number,
    created_at,
    payment_method,
    items: { lab_tests = [], platform_fee: platformFeeItems = [] } = {}
  } = invoiceData;

  const configuredHospital = useHospitalDetails();
  const hospitalSource = {
    ...(invoiceData?.hospital_details || {}),
    hospital_gst_number: invoiceData?.hospital_gst_number,
    ...(configuredHospital || {})
  };
  const hospitalDetails = normalizeHospitalDetails(hospitalSource);
  const hospitalLogo = pickHospitalLogo(hospitalSource, logo);
  const hospitalPhone = pickHospitalPhone(hospitalSource);

  const invoiceDate = new Date(created_at);
  const formattedDate = format(invoiceDate, 'dd-MM-yyyy');
  const formattedTime = format(invoiceDate, 'HH:mm');
  const insurerName = getInvoiceInsurerName(invoiceData);
  const visitDate = getInvoiceVisitDate(invoiceData);
  
  let doctorInfo = null;
  if (invoiceData.other_details) {
    try {
      const additionalInfo = typeof invoiceData.other_details === 'string' 
        ? JSON.parse(invoiceData.other_details) 
        : invoiceData.other_details;
      if (additionalInfo.doctor_id) {
        doctorInfo = invoiceData.doctor || { id: additionalInfo.doctor_id, full_name: additionalInfo.doctor_name || 'N/A' };
      }
    } catch (e) {
      console.error('Error parsing other_details:', e);
    }
  }
  
  if (invoiceData.doctor && !doctorInfo) {
    doctorInfo = invoiceData.doctor;
  }
  
  const processedItems = () => {
    if (!lab_tests.length) return { activeItems: [], cancelledItems: [] };

    const hasStatusField = lab_tests.some(item => item.status);
    if (!hasStatusField) {
      return { activeItems: lab_tests, cancelledItems: [] };
    }

    const hasCancelledItems = lab_tests.some(item => item.status === 'cancelled');

    if (hasCancelledItems) {
      const itemsMap = {};
      lab_tests.forEach(item => {
        const itemId = item.id;
        if (!itemsMap[itemId]) {
          itemsMap[itemId] = {
            ...item,
            activeQuantity: 0,
            cancelledQuantity: 0
          };
        }
        if (item.status === 'active') {
          itemsMap[itemId].activeQuantity = Number(item.quantity || 1);
        } else if (item.status === 'cancelled') {
          itemsMap[itemId].cancelledQuantity += Number(item.quantity || 1);
        }
      });

      const activeItems = Object.values(itemsMap)
        .map(item => ({
          ...item,
          quantity: item.activeQuantity - item.cancelledQuantity
        }))
        .filter(item => item.quantity > 0);

      return {
        activeItems,
        cancelledItems: lab_tests.filter(item => item.status === 'cancelled')
      };
    }

    return {
      activeItems: lab_tests.filter(item => item.status === 'active'),
      cancelledItems: []
    };
  };

  const { activeItems, cancelledItems } = processedItems();

  const calcFinal = (price, discount) => price * (1 - discount / 100);
  
  const subtotalBeforeDiscount = activeItems.reduce((sum, item) => sum + (Number(item.selling_price || 0) * Number(item.quantity || 1)), 0);
  const discountPercent = Number(invoiceData.discount || 0);
  
  const explicitActualDiscount = invoiceData.actualDiscount != null && invoiceData.actualDiscount !== '' 
    ? Number(invoiceData.actualDiscount) 
    : null;
    
  const totalDiscountAmount = Number(
    explicitActualDiscount !== null 
      ? explicitActualDiscount 
      : (subtotalBeforeDiscount > 0 ? (subtotalBeforeDiscount * discountPercent) / 100 : 0)
  );

  const actualDiscountPercentage = subtotalBeforeDiscount > 0 
    ? (totalDiscountAmount / subtotalBeforeDiscount) * 100 
    : 0;

  const subtotalAfterDiscount = subtotalBeforeDiscount - totalDiscountAmount;
  const cancelledTotal = cancelledItems.reduce((sum, item) => 
    sum + calcFinal(Number(item.selling_price || 0) * Number(item.quantity || 1), Number(item.discount_in_perc || 0)), 0);

  const subtotal = subtotalBeforeDiscount;
  
  const finalAmountAfterDiscount = subtotalAfterDiscount;
  
  // Online bookings bill the platform fee as its own item; it is already in the grand total.
  const platformFee = platformFeeItems.reduce((sum, item) => sum + Number(item.net_price ?? item.selling_price ?? 0), 0);
  const roundOff = invoiceData.roundOff !== undefined 
    ? invoiceData.roundOff 
    : Math.abs(Number(invoiceData.round_off || 0));
    
  const isPositiveRoundOff = invoiceData.isPositiveRoundOff !== undefined 
    ? invoiceData.isPositiveRoundOff 
    : (Number(invoiceData.original_amount || 0) > Number(invoiceData.total_amount || 0));
  
  const grandTotal = invoiceData.finalTotal !== undefined 
    ? invoiceData.finalTotal 
    : Number(invoiceData.total_amount || 0);

  const totalInWords = `Rs. ${numberToWords(Math.round(grandTotal))} Only`;
  const anyDiscount = activeItems.some(item => item.discount_in_perc > 0);

  const groupedCancelledEvents = (() => {
    if (!cancelledItems || cancelledItems.length === 0) return [];

    const eventsMap = {};
    cancelledItems.forEach(item => {
      const ledgerId = item.cancellation_ledger_id || 'unknown';
      const timeStr = item.created_at ? format(new Date(item.created_at), 'dd-MM-yyyy HH:mm') : 'N/A';
      const staffName = item.cancelled_by_name || 'N/A';

      if (!eventsMap[ledgerId]) {
        eventsMap[ledgerId] = {
          time: timeStr,
          staffName: staffName,
          items: []
        };
      }
      eventsMap[ledgerId].items.push(item);
    });
    return Object.values(eventsMap);
  })();

  return (
    <Box>
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', gap: 1, '@media print': { display: 'none' } }}>
        <IconButton color="secondary" onClick={handlePrint}>
            <PrintIcon />
        </IconButton>
        <IconButton color="error" onClick={onClose}>
            <CloseIcon />
        </IconButton>
      </Box>

        <Box sx={{ p: 3, '@media print': { p: 1 } }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box
                    component="img"
                    src={hospitalLogo}
                    alt="Hospital Logo"
                    sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        display: 'block',
                        mb: 1
                    }}
                />
                <Typography variant="h5" gutterBottom>
                    {hospitalDetails.name}
                </Typography>
                {hospitalDetails.address && (
                    <Typography variant="body2">{hospitalDetails.address}</Typography>
                )}
                {hospitalPhone && (
                  <Typography variant="body2">Phone: {hospitalPhone}</Typography>
                )}
                {hospitalDetails.gstNumber && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Hospital GST:</strong> {hospitalDetails.gstNumber}
                    </Typography>
                )}
            </Box>

            {/* Patient & Invoice Info */}
            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <Typography><strong>Patient:</strong> {customerInfo?.full_name || 'N/A'} ({customerInfo?.id || 'N/A'})</Typography>
                    <Typography><strong>Date & Time:</strong> {formattedDate} {formattedTime}</Typography>
                    {visitDate && <Typography><strong>Visit Date:</strong> {format(parseISO(visitDate), 'dd-MM-yyyy')}</Typography>}
                    {insurerName && <Typography><strong>Insurance:</strong> {insurerName}</Typography>}
                    <Typography><strong>Staff:</strong> {customerInfo?.created_by} ({customerInfo?.id || 'N/A'})</Typography>
                    {doctorInfo && (
                      <Typography><strong>Doctor:</strong> {doctorInfo.full_name} ({doctorInfo.id})</Typography>
                    )}
                </Grid>
                <Grid item xs={6}>
                    <Typography align="right"><strong>Bill No:</strong> {invoice_number}</Typography>
                    <Typography align="right"><strong>Payment Method:</strong> {payment_method === 'hybrid' ? 'Cash + Online' : (_.capitalize(payment_method) || 'N/A')}</Typography>
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Active Lab Tests Section */}
            {activeItems.length > 0 && (
                <>
                    {cancelledItems.length > 0 && (
                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Active Lab Tests</Typography>
                    )}
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">MRP</TableCell>
                                    {anyDiscount && <TableCell align="right">Disc%</TableCell>}
                                    <TableCell align="right">Amt</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {activeItems.map((item, index) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell align="right">{item.quantity || 1}</TableCell>
                                        <TableCell align="right">{parseFloat(item.selling_price).toFixed(2)}</TableCell>
                                        {anyDiscount && (
                                            <TableCell align="right">{parseFloat(item.discount_in_perc || 0).toFixed(2)}%</TableCell>
                                        )}
                                        <TableCell align="right">
                                            {calcFinal(item.selling_price * (item.quantity || 1), item.discount_in_perc).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* Cancelled Lab Tests Section */}
            {groupedCancelledEvents.length > 0 && (() => {
                const grandCancelTotal = groupedCancelledEvents.reduce((gsum, evt) =>
                    gsum + evt.items.reduce((sum, item) =>
                        sum + calcFinal(Number(item.selling_price || 0) * Number(item.quantity || 1), Number(item.discount_in_perc || 0)), 0), 0);
                const colCount = 4 + (anyDiscount ? 1 : 0);

                return (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 2, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                Sales Returns ({groupedCancelledEvents.length} event{groupedCancelledEvents.length > 1 ? 's' : ''})
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                Total Returned: ₹{grandCancelTotal.toFixed(2)}
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>#</strong></TableCell>
                                        <TableCell><strong>Description</strong></TableCell>
                                        <TableCell align="right"><strong>Qty</strong></TableCell>
                                        <TableCell align="right"><strong>MRP</strong></TableCell>
                                        {anyDiscount && <TableCell align="right"><strong>Disc%</strong></TableCell>}
                                        <TableCell align="right"><strong>Amt</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groupedCancelledEvents.map((evt, evtIdx) => {
                                        const eventTotal = evt.items.reduce((sum, item) =>
                                            sum + calcFinal(Number(item.selling_price || 0) * Number(item.quantity || 1), Number(item.discount_in_perc || 0)), 0);
                                        return (
                                            <>
                                                {/* Lightweight event header row — no box */}
                                                <TableRow key={`evt-hdr-${evtIdx}`} sx={{ backgroundColor: '#fef2f2' }}>
                                                    <TableCell colSpan={Math.ceil(colCount / 2)} sx={{ py: '3px', fontWeight: 'bold', fontSize: '0.75rem', color: 'error.main' }}>
                                                        Return #{evtIdx + 1} — {evt.time}
                                                    </TableCell>
                                                        <TableCell colSpan={Math.floor(colCount / 2) + 1} align="right" sx={{ py: '3px', fontSize: '0.75rem', color: 'text.secondary' }}>
                                                        By: {evt.staffName || 'N/A'}
                                                    </TableCell>
                                                </TableRow>
                                                {evt.items.map((item, index) => (
                                                    <TableRow key={item.id}>
                                                        <TableCell>{index + 1}</TableCell>
                                                        <TableCell>{item.name}</TableCell>
                                                        <TableCell align="right">{item.quantity || 1}</TableCell>
                                                        <TableCell align="right">{parseFloat(item.selling_price).toFixed(2)}</TableCell>
                                                        {anyDiscount && (
                                                            <TableCell align="right">{parseFloat(item.discount_in_perc || 0).toFixed(2)}%</TableCell>
                                                        )}
                                                        <TableCell align="right">
                                                            {calcFinal(item.selling_price * (item.quantity || 1), item.discount_in_perc).toFixed(2)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {groupedCancelledEvents.length > 1 && (
                                                    <TableRow key={`evt-total-${evtIdx}`} sx={{ backgroundColor: '#fff5f5' }}>
                                                        <TableCell colSpan={colCount} align="right" sx={{ py: '2px', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                                            Return #{evtIdx + 1} subtotal: <strong>₹{eventTotal.toFixed(2)}</strong>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </>
                                        );
                                    })}
                                    {/* Grand total row */}
                                    <TableRow sx={{ borderTop: '2px solid black', backgroundColor: '#fff0f0' }}>
                                        <TableCell colSpan={colCount} align="right" sx={{ fontWeight: 'bold', py: '4px' }}>
                                            Total Returned: ₹{grandCancelTotal.toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                );
            })()}



            <Divider sx={{ mb: 2 }} />

            <Grid container justifyContent="flex-end">
                <Grid item xs={12} sm={5} md={4}>
                    <Stack spacing={1}>
                        <Stack 
                          direction="row" 
                          justifyContent="space-between"
                          sx={{ 
                            py: 0.5, 
                            px: 1, 
                            backgroundColor: '#f5f5f5', 
                            borderTop: '1px solid black', 
                            borderBottom: '1px solid black',
                            fontWeight: 'bold'
                          }}
                        >
                            <Typography variant="subtitle2" fontWeight={700}>Subtotal (Before Discount):</Typography>
                            <Typography variant="subtitle2" fontWeight={700}>₹ {subtotal.toFixed(2)}</Typography>
                        </Stack>
                        {anyDiscount && (
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="textSecondary">(-) Discount ({actualDiscountPercentage.toFixed(2)}%):</Typography>
                                <Typography>₹ {totalDiscountAmount.toFixed(2)}</Typography>
                            </Stack>
                        )}
                        <Divider sx={{ borderStyle: 'dashed' }} />
                        <Stack direction="row" justifyContent="space-between">
                            <Typography color="textSecondary">Subtotal (After Discount):</Typography>
                            <Typography>₹ {finalAmountAfterDiscount.toFixed(2)}</Typography>
                        </Stack>
                        {cancelledItems.length > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="textSecondary">(-) Cancelled Items (+ Discounted):</Typography>
                                <Typography>₹ {cancelledTotal.toFixed(2)}</Typography>
                            </Stack>
                        )}
                        {platformFee > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="textSecondary">(+) Online Platform Fee:</Typography>
                                <Typography>₹ {platformFee.toFixed(2)}</Typography>
                            </Stack>
                        )}
                        {roundOff > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="textSecondary">Round Off:</Typography>
                                <Typography>{isPositiveRoundOff ? '+' : '-'}₹ {roundOff.toFixed(2)}</Typography>
                            </Stack>
                        )}
                        <Divider />
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h6" color="textSecondary">Final Amount to Pay (Grand Total):</Typography>
                            <Typography variant="h6">₹ {grandTotal.toFixed(2)}</Typography>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>

            <Typography variant="body2" sx={{ mt: 2 }}>
                {totalInWords}
            </Typography>

            <Box sx={{ mt: 4, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Typography variant="body2" align="center">
                नर सेवा नारायण सेवा
                </Typography>
            </Box>
        </Box>
    </Box>
  );
}

LabInvoiceA4Content.propTypes = {
  customerInfo: PropTypes.object.isRequired,
  invoiceData: PropTypes.object.isRequired
};
